import { Component, OnInit, ViewChild, AfterViewInit, OnDestroy } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatSort, Sort, SortDirection } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { DataService, PagedResult } from '../../data.service';
import { Element } from '../../../element.model';
import { ElementDialogComponent } from '../element-dialog/element-dialog.component';
import { ConfirmDeleteDialogComponent } from '../confirm-delete-dialog/confirm-delete-dialog.component';
import { DialogElementeAddComponent } from '../dialog-elemente-add/dialog-elemente-add.component';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { ElementWithdrawalService } from '../../../Services/element-withdrawal.service';
import { ElementWithdrawalDialogComponent } from '../../elementwithdraw/element-withdrawal-dialog/element-withdrawal-dialog.component';
import { trigger, transition, style, animate } from '@angular/animations';
import { AuthService } from '../../../api-authorization/authorize.service';

@Component({
  selector: 'app-elemente-liste',
  templateUrl: './elemente-liste.component.html',
  styleUrls: ['./elemente-liste.component.css'],
  animations: [
    trigger('slideDown', [
      transition(':enter', [
        style({ height: 0, opacity: 0, overflow: 'hidden' }),
        animate('250ms ease-out', style({ height: '*', opacity: 1 }))
      ]),
      transition(':leave', [
        animate('200ms ease-in', style({ height: 0, opacity: 0, overflow: 'hidden' }))
      ])
    ])
  ]
})
export class ElementeListeComponent implements OnInit, AfterViewInit, OnDestroy {
  // Datenquelle und Paginierung
  dataSource = new MatTableDataSource<Element>([]);
  elemente: Element[] = [];
  totalCount: number = 0;
  currentPage: number = 1;
  pageSize: number = 19;
  isLoading: boolean = false;

  // Sortierungsvariablen
  sortActive: string = 'name';
  sortDirection: SortDirection = 'asc';

  // Angezeigte Spalten und Spaltendefinitionen
  displayedColumns: string[] = ['name', 'description', 'hersteller', 'kategorie', 'bestellnumer', 'lager_lagerort', 'quantity', 'actions', 'editQuantity'];

  // Angepasste columnNames: Nur Spalten, die in displayedColumns enthalten sind
  columnNames = [
    { id: 'name', value: 'NAME', sortable: true },
    { id: 'description', value: 'BESCHREIBUNG', sortable: true },
    { id: 'hersteller', value: 'HERSTELLER', sortable: true },
    { id: 'kategorie', value: 'KATEGORIE', sortable: true },
    { id: 'bestellnumer', value: 'BESTELLNUMMER', sortable: true },
    { id: 'lager_lagerort', value: 'LAGER / LAGERORT', sortable: true },
    { id: 'quantity', value: 'MENGE', sortable: true }
  ];

  // Erweiterte Filter
  advancedFilters: any = {
    name: '',
    description: '',
    hersteller: '',
    kategorie: '',
    bestellnumer: '',
    zustand: '',
    lager_lagerort: ''
  };
  showAdvancedFilters: boolean = false;

  // Filter-Steuerelemente
  filterControl = new FormControl('');
  selectedCategory: string | null = null;
  selectedLager: string | null = null;
  categories: string[] = [];
  lager: string[] = [];
  lagerort: string[] = [];

  // Subject für debounced Suche
  private searchSubject = new Subject<string>();
  private subscriptions: Subscription[] = [];

  //Responisve Layout
  viewMode: 'table' | 'card' = (typeof window !== 'undefined' && window.innerWidth <= 768) ? 'card' : 'table';
  private breakpointSubscription!: Subscription;

  // Suchvorschläge
  searchSuggestions: string[] = [];
  private suggestionsLoading = false;

  // ViewChild Referenzen
  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild(MatPaginator) paginator!: MatPaginator;

  constructor(private dataService: DataService, public dialog: MatDialog, private snackBar: MatSnackBar, private breakpointObserver: BreakpointObserver, private withdrawalService: ElementWithdrawalService, private authService: AuthService) { }

  /** Darf der aktuelle Benutzer Stammdaten verändern? (Montage = nur lesen) */
  get canEdit(): boolean {
    return this.authService.canEdit();
  }

  ngOnInit(): void {
    // Kategorien und Lager laden
    this.loadFilterOptions();

    this.breakpointSubscription = this.breakpointObserver
      .observe(['(max-width: 768px)'])
      .subscribe(result => {
        this.viewMode = result.matches ? 'card' : 'table';
      });

    // Debounced Suche für den Hauptfilter einrichten
    const searchSubscription = this.searchSubject.pipe(
      debounceTime(400),
      distinctUntilChanged()
    ).subscribe(term => {
      this.currentPage = 1;
      if (this.paginator) {
        this.paginator.pageIndex = 0;
      }
      this.loadPage();
    });
    this.subscriptions.push(searchSubscription);

    // Filteränderungen überwachen
    const filterSubscription = this.filterControl.valueChanges.subscribe(value => {
      this.searchSubject.next(value || '');
      this.loadSearchSuggestions(value || '');
    });
    this.subscriptions.push(filterSubscription);

    // Erste Seite laden
    this.loadPage();
  }

  ngAfterViewInit(): void {
    // Die Verzögerung ist notwendig, da Angular den Paginator erst nach der ersten Change Detection initialisiert
    setTimeout(() => {
      // Paginator mit DataSource verbinden, wenn verfügbar
      if (this.paginator) {
        // Wir verwenden nicht this.dataSource.paginator = this.paginator
        // Stattdessen aktualisieren wir den Paginator in loadPage() manuell
        console.log('Paginator initialized');
      }
    }, 0);
  }

  // Manuelle Sortierung der aktuellen Daten
  sortDataManually(): void {
    if (this.sortActive && this.sortDirection) {
      this.elemente = this.sortData(this.elemente, this.sortActive, this.sortDirection);
      this.dataSource.data = this.elemente;
    }
  }

  ngOnDestroy(): void {
    // Alle Subscriptions aufräumen
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
    if (this.breakpointSubscription) {
      this.breakpointSubscription.unsubscribe();
    }
  }

  openWithdrawalDialog(element: Element): void {
    const dialogRef = this.dialog.open(ElementWithdrawalDialogComponent, {
      width: 'auto',
      data: { element: { ...element } }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        // Aktualisiere die Anzeige nach einer Entnahme
        this.loadPage();
      }
    });
  };
  

  toggleViewManually() {
    this.viewMode = this.viewMode === 'table' ? 'card' : 'table';
  }

  /** Suchvorschläge aus dem Backend laden */
  private loadSearchSuggestions(term: string): void {
    if (!term || term.length < 2) {
      this.searchSuggestions = [];
      return;
    }
    if (this.suggestionsLoading) return;
    this.suggestionsLoading = true;

    this.dataService.getElementePaged(
      1, 20, term, this.selectedLager, null, null, null, 'name', 'asc'
    ).subscribe({
      next: (result) => {
        const seen = new Set<string>();
        const suggestions: string[] = [];
        const lowerTerm = term.toLowerCase();

        for (const el of result.items) {
          // Name
          if (el.name && el.name.toLowerCase().includes(lowerTerm) && !seen.has(el.name)) {
            seen.add(el.name);
            suggestions.push(el.name);
          }
          // Hersteller
          if (el.hersteller && el.hersteller.toLowerCase().includes(lowerTerm) && !seen.has(el.hersteller)) {
            seen.add(el.hersteller);
            suggestions.push(el.hersteller);
          }
          // Bestellnummer
          if (el.bestellnumer && el.bestellnumer.toLowerCase().includes(lowerTerm) && !seen.has(el.bestellnumer)) {
            seen.add(el.bestellnumer);
            suggestions.push(el.bestellnumer);
          }
          // Kategorie
          if (el.kategorie && el.kategorie.toLowerCase().includes(lowerTerm) && !seen.has(el.kategorie)) {
            seen.add(el.kategorie);
            suggestions.push(el.kategorie);
          }
          if (suggestions.length >= 8) break;
        }
        this.searchSuggestions = suggestions;
        this.suggestionsLoading = false;
      },
      error: () => {
        this.searchSuggestions = [];
        this.suggestionsLoading = false;
      }
    });
  }

  /** Vorschlag auswählen */
  onSuggestionSelected(value: string): void {
    this.filterControl.setValue(value);
  }

  private loadFilterOptions(): void {
    // Kategorien laden
    this.dataService.getCategories().subscribe(
      categories => {
        this.categories = categories;
      },
      error => {
        console.error('Fehler beim Laden der Kategorien:', error);
        this.snackBar.open('Fehler beim Laden der Kategorien.', 'Schließen', { duration: 3000 });
      }
    );

    // Lagerplätze laden
    this.dataService.getWarehouses().subscribe(
      warehouses => {
        this.lager = ['Alle', ...warehouses];
      },
      error => {
        console.error('Fehler beim Laden der Lagerplätze:', error);
        this.snackBar.open('Fehler beim Laden der Lagerplätze.', 'Schließen', { duration: 3000 });
      }
    );
  }
  loadPage(): void {
    this.isLoading = true;
    console.log('Loading page:', this.currentPage);
    console.log('Sort active:', this.sortActive);
    console.log('Sort direction:', this.sortDirection);
    console.log('Selected category:', this.selectedCategory);
    console.log('Selected lager:', this.selectedLager);

    // Extrahiere Hersteller- und Lagerort-Filter aus den erweiterten Filtern
    let selectedHersteller: string | null = null;
    let selectedLagerort: string | null = null;

    // Erstellen Sie einen zusammengesetzten Suchbegriff, der die erweiterten Filter enthält
    let enhancedSearchTerm = this.filterControl.value || '';

    // Fügen Sie die erweiterten Filter hinzu, wenn sie aktiviert sind
    if (this.showAdvancedFilters) {
      const activeFilters = [];

      if (this.advancedFilters.name) {
        activeFilters.push(`${this.advancedFilters.name}`);
      }

      if (this.advancedFilters.description) {
        activeFilters.push(`${this.advancedFilters.description}`);
      }

      if (this.advancedFilters.hersteller) {
        // Statt zum Suchbegriff hinzuzufügen, verwenden wir den dedizierten Filter
        selectedHersteller = this.advancedFilters.hersteller;
        console.log('Setting hersteller filter:', selectedHersteller);
      }

      if (this.advancedFilters.kategorie) {
        this.selectedCategory = this.advancedFilters.kategorie;
        console.log('Setting category from advanced filter:', this.selectedCategory);
      }

      if (this.advancedFilters.bestellnumer) {
        activeFilters.push(`${this.advancedFilters.bestellnumer}`);
      }

      if (this.advancedFilters.zustand) {
        activeFilters.push(`${this.advancedFilters.zustand}`);
      }

      // WICHTIG: Korrigierte Verarbeitung des lager_lagerort Felds
      if (this.advancedFilters.lager_lagerort && this.advancedFilters.lager_lagerort.trim() !== '') {
        // Versuche, den Lager- und Lagerort-Teil zu trennen
        const lagerLagerortText = this.advancedFilters.lager_lagerort.trim();

        if (lagerLagerortText.includes('/')) {
          // Format ist "Lager / Lagerort"
          const parts = lagerLagerortText.split('/');
          if (parts.length >= 2) {
            // Wenn kein Lager gesetzt ist, könnten wir auch den Lager-Teil setzen
            if (this.selectedLager === null && parts[0].trim() !== '-') {
              this.selectedLager = parts[0].trim();
              console.log('Setting lager from lager_lagerort:', this.selectedLager);
            }

            // Extrahiere den Lagerort (nach dem /)
            if (parts[1].trim() !== '-') {
              selectedLagerort = parts[1].trim();
              console.log('Setting lagerort from lager_lagerort:', selectedLagerort);
            }
          }
        } else {
          // Wenn kein Slash vorhanden ist, könnte es nur ein Lagerort sein
          // Wir fügen ihn zum searchTerm hinzu, da wir nicht sicher sind, ob es Lager oder Lagerort ist
          activeFilters.push(lagerLagerortText);
          console.log('Adding lager_lagerort text to search term:', lagerLagerortText);
        }
      }

      // Kombinieren Sie die verbleibenden Filter mit dem Hauptsuchbegriff
      if (activeFilters.length > 0) {
        if (enhancedSearchTerm) {
          enhancedSearchTerm += ' ';
        }
        enhancedSearchTerm += activeFilters.join(' ');
      }
    }

    console.log('Final filter parameters:', {
      searchTerm: enhancedSearchTerm,
      category: this.selectedCategory,
      lager: this.selectedLager,
      hersteller: selectedHersteller,
      lagerort: selectedLagerort
    });
    

    // Daten mit den aktuellen Parametern laden
    this.dataService.getElementePaged(
      this.currentPage,
      this.pageSize,
      enhancedSearchTerm,
      this.selectedLager,
      this.selectedCategory,
      selectedHersteller,
      selectedLagerort,  // Passing lagerort as separate parameter now
      this.sortActive,
      this.sortDirection
    ).subscribe(
      (result: PagedResult<Element>) => {
        console.log('Server response:', {
          items: result.items.length,
          totalCount: result.totalCount,
          page: result.page,
          pageSize: result.pageSize,
          totalPages: result.totalPages
        });

        // Update elements array and DataSource
        this.elemente = result.items;
        this.dataSource.data = this.elemente;

        // Gesamtanzahl setzen
        this.totalCount = result.totalCount;

        // Direktes Update des Paginators
        if (this.paginator) {
          this.paginator.length = result.totalCount;
          this.paginator.pageSize = result.pageSize;
          this.paginator.pageIndex = result.page - 1; // Angular verwendet 0-Index
        }
        this.loadLagerorteForSelectedLager();
        this.isLoading = false;
      },
      error => {
        console.error('Error loading elements:', error);
        this.snackBar.open('Fehler beim Laden der Daten.', 'Schließen', { duration: 3000 });
        this.isLoading = false;
      }
    );
  }

  applyLagerortFilter(lagerort: string | null): void {
    console.log('Applying lagerort filter:', lagerort);

    // Aktualisiere die aktuelle Ansicht der erweiterten Filter
    if (lagerort) {
      // Wenn ein Lagerort ausgewählt ist, aktualisiere das erweiterte Filter-Feld
      // Aber behalte den Lager-Teil bei, falls er bereits gesetzt ist
      const lagerTeil = this.selectedLager ? this.selectedLager : '-';
      this.advancedFilters.lager_lagerort = `${lagerTeil} / ${lagerort}`;
    } else {
      // Wenn kein Lagerort ausgewählt ist, setze nur den Lager-Teil
      if (this.selectedLager) {
        this.advancedFilters.lager_lagerort = `${this.selectedLager} / -`;
      } else {
        this.advancedFilters.lager_lagerort = '';
      }
    }

    // Lade die Seite neu
    this.currentPage = 1;
    if (this.paginator) {
      this.paginator.pageIndex = 0;
    }
    this.loadPage();
  }


  loadLagerorteForSelectedLager(): void {
    if (this.selectedLager && this.selectedLager !== 'Alle') {
      this.dataService.getLagerorte(this.selectedLager).subscribe(
        lagerorte => {
          this.lagerort = lagerorte;
          console.log('Loaded lagerorte for', this.selectedLager, ':', this.lagerort);
        },
        error => {
          console.error('Error loading lagerorte:', error);
          this.lagerort = [];
        }
      );
    } else {
      // Wenn kein Lager ausgewählt ist, leere die Lagerort-Liste
      this.dataService.getLagerorte("Alle").subscribe(
        lagerorte => {
          this.lagerort = lagerorte;
          console.log('Loaded lagerorte for', this.selectedLager, ':', this.lagerort);
        },
        error => {
          console.error('Error loading lagerorte:', error);
          this.lagerort = [];
        }
      );
    }
  }



  // Hilfsmethode für die client-seitige Sortierung
  sortData(data: Element[], active: string, direction: SortDirection): Element[] {
    console.log('Manually sorting data by:', active, direction);

    if (!active || direction === '') {
      return data;
    }

    return [...data].sort((a, b) => {
      // Werte aus den Objekten extrahieren
      let valueA: any;
      let valueB: any;

      // Zugriff über direktes Indexing
      valueA = a[active];
      valueB = b[active];

      // Konvertieren Sie in Zahlen für numerische Felder
      if (active === 'quantity' || active === 'id') {
        valueA = Number(valueA) || 0;
        valueB = Number(valueB) || 0;
      } else {
        // Für String-Werte, behandeln Sie null/undefined als leeren String
        valueA = valueA !== null && valueA !== undefined ? valueA : '';
        valueB = valueB !== null && valueB !== undefined ? valueB : '';
      }

      // Führen Sie den Vergleich durch
      const comparison = typeof valueA === 'number'
        ? (valueA - valueB)
        : String(valueA).localeCompare(String(valueB));

      // Wenden Sie die Sortierrichtung an
      return direction === 'asc' ? comparison : -comparison;
    });
  }

  // Manuelle Spalten-Sortierung
  // Update sortByColumn to reload the data from the server with new sort parameters
  sortByColumn(columnId: string): void {
    console.log('Manual sort by column clicked:', columnId);

    // Sortierlogik: asc -> desc -> keine Sortierung
    if (this.sortActive === columnId) {
      if (this.sortDirection === 'asc') {
        this.sortDirection = 'desc';
      } else {
        this.sortDirection = 'asc';
      }
    } else {
      this.sortActive = columnId;
      this.sortDirection = 'asc';
    }

    // Instead of manually sorting, reload the data from the server
    this.loadPage();
  }

  // Methode zum Umschalten der erweiterten Filter
  toggleAdvancedFilters(): void {
    this.showAdvancedFilters = !this.showAdvancedFilters;
  }

  // Methode zum Anwenden der erweiterten Filter
  applyAdvancedFilters(): void {
    this.currentPage = 1;
    if (this.paginator) {
      this.paginator.pageIndex = 0;
    }
    this.loadPage();
  }

  // Methode zum Zurücksetzen der erweiterten Filter
  resetAdvancedFilters(): void {
    this.advancedFilters = {
      name: '',
      description: '',
      hersteller: '',
      kategorie: '',
      bestellnumer: '',
      zustand: '',
      lager_lagerort: ''
    };
  }

  // Paginierungs-Event-Handler
  onPageChange(event: PageEvent): void {
    console.log('Page change event:', event);
    this.currentPage = event.pageIndex + 1;
    this.pageSize = event.pageSize;
    this.loadPage();
  }

  // Bearbeitungsmodus umschalten
  toggleEditMode(element: Element) {
    if (element.editMode) {
      this.dataService.updateElement(element.id, element).subscribe(
        () => {
          this.loadPage(); // Aktuelle Seite neu laden
        },
        error => {
          console.error('Error updating element:', error);
          this.snackBar.open('Fehler beim Aktualisieren des Elements.', 'Schließen', { duration: 3000 });
        }
      );
    }
    element.editMode = !element.editMode;
  }

  // Dialog zum Bearbeiten öffnen
  openEditDialog(element?: Element, isCopy: boolean = false): void {
    let dialogData: any;

    if (element) {
      if (isCopy) {
        const elementCopy = { ...element, id: 0 };
        dialogData = { element: elementCopy };
      } else {
        dialogData = { element: { ...element } };
      }
    } else {
      dialogData = {
        newElement: { id: 0, name: '', description: '', hersteller: '', kategorie: '', zustand: '', bestellnumer: '', barcodetyp: '', quantity: 0, editMode: false },
        categories: this.categories,
        lager: this.lager,
        lagerort: []
      };
    }

    const dialogRef = this.dialog.open(ElementDialogComponent, {
      width: 'auto',
      data: {
        ...dialogData,
        iscopy: isCopy
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        if (element && !isCopy) {
          this.dataService.updateElement(element.id, result).subscribe(
            updatedElement => {
              if (updatedElement) {
                this.loadPage(); // Aktuelle Seite neu laden
                this.snackBar.open('Element erfolgreich gespeichert!', 'Schließen', { duration: 3000 });
              } else {
                this.snackBar.open('Fehler beim Speichern des Elements.', 'Schließen', { duration: 3000 });
              }
            },
            error => {
              console.error('Error updating element:', error);
              this.snackBar.open('Fehler beim Speichern des Elements.', 'Schließen', { duration: 3000 });
            }
          );
        } else {
          this.dataService.addElement(result).subscribe(
            () => {
              this.loadPage(); // Aktuelle Seite neu laden
              this.snackBar.open(isCopy ? 'Kopie erfolgreich erstellt!' : 'Element erfolgreich hinzugefügt!', 'Schließen', { duration: 3000 });
            },
            error => {
              console.error('Error adding element:', error);
              this.snackBar.open('Fehler beim Hinzufügen!', 'Schließen', { duration: 3000 });
            }
          );
        }
      }
    });
  }

  // Kategorie-Filter anwenden
  applyCategoryFilter(category: string | null): void {
    this.selectedCategory = category;
    this.currentPage = 1; // Zurück zur ersten Seite beim Filtern
    if (this.paginator) {
      this.paginator.pageIndex = 0;
    }
    this.loadPage();
  }

  // Lager-Filter anwenden
  applyLagerFilter(lager: string | null): void {
    // Setze den Lager-Filter
    if (lager === 'Alle') {
      this.selectedLager = null;
    } else {
      this.selectedLager = lager;
    }

    // Wenn sich das Lager ändert, aktualisiere die Lagerorte
    this.loadLagerorteForSelectedLager();

    // Aktualisiere den Lagerort-Teil in den erweiterten Filtern
    if (this.advancedFilters.lager_lagerort && this.advancedFilters.lager_lagerort.includes('/')) {
      // Behalte den Lagerort-Teil bei, aktualisiere nur den Lager-Teil
      const parts = this.advancedFilters.lager_lagerort.split('/');
      const lagerortPart = parts.length > 1 ? parts[1].trim() : '-';
      this.advancedFilters.lager_lagerort = `${this.selectedLager || '-'} / ${lagerortPart}`;
    } else {
      // Setze ein neues Lager/Lagerort-Format
      this.advancedFilters.lager_lagerort = `${this.selectedLager || '-'} / -`;
    }

    // Zurück zur ersten Seite beim Filtern
    this.currentPage = 1;
    if (this.paginator) {
      this.paginator.pageIndex = 0;
    }

    // Lade die Daten
    this.loadPage();
  }

  // Alle Filter zurücksetzen
  deleteFilter(): void {
    this.filterControl.setValue('');
    this.selectedCategory = null;
    this.selectedLager = null;
    this.resetAdvancedFilters();
    this.currentPage = 1;
    if (this.paginator) {
      this.paginator.pageIndex = 0;
    }
    this.loadPage();
  }

  // Element löschen
  onDeleteClick(element: Element): void {
    const dialogRef = this.dialog.open(ConfirmDeleteDialogComponent, {
      width: '350px',
      data: { elementId: element.id, elementName: element.name }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.dataService.deleteElement(element.id).subscribe(
          () => {
            this.loadPage(); // Daten nach dem Löschen neu laden
            this.snackBar.open('Element erfolgreich gelöscht!', 'Schließen', {
              duration: 3000
            });
          },
          error => {
            console.error('Fehler beim Löschen des Elements:', error);
            this.snackBar.open('Fehler beim Löschen des Elements.', 'Schließen', {
              duration: 3000
            });
          }
        );
      }
    });
  }

  // Kategorieänderung behandeln
  onCategoryChange(category: string | null): void {
    this.applyCategoryFilter(category);
  }

  // Lageränderung behandeln
  onLagerChange(index: number): void {
    if (index === 0) {
      this.selectedLager = null;
    } else {
      this.selectedLager = this.lager[index];
    }
    this.currentPage = 1; // Zurück zur ersten Seite
    if (this.paginator) {
      this.paginator.pageIndex = 0;
    }
    this.loadPage();
  }

  // Hinzufügen-Button-Klick behandeln
  onAddClick(): void {
    // Gefilterte Lagerorte für das ausgewählte Lager abrufen
    let filteredLagerorte: string[] = [];

    if (this.selectedLager) {
      this.dataService.getLagerorte(this.selectedLager).subscribe(
        lagerorte => {
          filteredLagerorte = lagerorte;
          this.openAddDialog(filteredLagerorte);
        },
        error => {
          console.error('Fehler beim Laden der Lagerorte:', error);
          this.openAddDialog(filteredLagerorte);
        }
      );
    } else {
      this.openAddDialog(filteredLagerorte);
    }
  }

  // Hilfsmethode zum Öffnen des Hinzufügen-Dialogs
  private openAddDialog(filteredLagerorte: string[]): void {
    const dialogRef = this.dialog.open(DialogElementeAddComponent, {
      data: {
        newElement: { id: 0, name: '', description: '', hersteller: '', kategorie: '', zustand: '', bestellnumer: '', barcodetyp: '', quantity: 0, editMode: false },
        categories: this.categories,
        lager: this.lager,
        lagerort: filteredLagerorte
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.dataService.addElement(result).subscribe(
          () => {
            this.loadPage(); // Daten nach dem Hinzufügen neu laden
            this.snackBar.open('Erfolgreich hinzugefügt!', 'Schließen', {
              duration: 3000
            });
          },
          error => {
            console.error('Fehler beim Hinzufügen des Elements:', error);
            this.snackBar.open('Fehler beim Hinzufügen!', 'Schließen', {
              duration: 3000
            });
          }
        );
      }
    });
  }
}
